/**
 * BBC Micro Sound Service
 *
 * Provides sound effect playback for AI-generated sound tags.
 * Uses the SN76489 sound chip emulator to play classic BBC Micro sounds.
 */

import { SN76489 } from '../lib/audio';

// Map of sound IDs to SN76489 methods
type SoundMap = Record<string, () => Promise<void>>;

class SoundService {
  private soundChip: SN76489 | null = null;
  private initialized = false;
  private soundMap: SoundMap = {};

  /**
   * Initialize the sound chip and create sound mappings
   */
  async init(): Promise<void> {
    if (this.initialized) return;

    this.soundChip = new SN76489();
    await this.soundChip.init();

    // Create mapping of sound IDs to SN76489 methods
    this.soundMap = {
      // System sounds
      'boot': () => this.soundChip!.startupBeep(),
      'error': () => this.soundChip!.errorBeep(),
      'ready': () => this.soundChip!.systemsOnline(),

      // Navigation & Travel
      'jump': () => this.soundChip!.hyperspaceJump(),
      'witchspace': () => this.soundChip!.witchSpace(),
      'galactic-jump': () => this.soundChip!.galacticHyperdrive(),
      'dock': () => this.soundChip!.dockingComplete(),
      'docking': () => this.soundChip!.dockingComplete(),
      'undock': () => this.soundChip!.dockingComplete(), // Could be different
      'nav-computer': () => this.soundChip!.navComputer(),

      // Combat
      'laser': () => this.soundChip!.laserFire(),
      'missile': () => this.soundChip!.missileLaunch(),
      'missile-lock': () => this.soundChip!.missileLock(),
      'ecm': () => this.soundChip!.ecmJamming(),
      'energy-bomb': () => this.soundChip!.energyBomb(),
      'shield-hit': () => this.soundChip!.shieldHit(),
      'shield-depleted': () => this.soundChip!.shieldDepleted(),
      'hull-damage': () => this.soundChip!.hullDamage(),
      'damage': () => this.soundChip!.hullDamage(),
      'explosion': () => this.soundChip!.explosion(),
      'bounty': () => this.soundChip!.bountyAwarded(),

      // Thargoids
      'thargoid': () => this.soundChip!.thargoidPresence(),
      'thargoid-attack': () => this.soundChip!.thargoidAttack(),

      // Mining & Cargo
      'mining': () => this.soundChip!.miningLaser(),
      'asteroid': () => this.soundChip!.asteroidBreak(),
      'scoop': () => this.soundChip!.cargoScoop(),
      'cargo': () => this.soundChip!.cargoCollected(),
      'cargo-collected': () => this.soundChip!.cargoCollected(),
      'fuel-scoop': () => this.soundChip!.fuelScoop(),
      'tractor': () => this.soundChip!.tractorBeam(),

      // Trading & Economy
      'trade': () => this.soundChip!.tradingSuccess(),
      'trade-success': () => this.soundChip!.tradingSuccess(),
      'trade-fail': () => this.soundChip!.tradingFailure(),
      'purchase': () => this.soundChip!.purchaseComplete(),
      'no-funds': () => this.soundChip!.insufficientFunds(),
      'equipment': () => this.soundChip!.equipmentInstalled(),
      'collect': () => this.soundChip!.collectItem(),
      'credits': () => this.soundChip!.purchaseComplete(), // Use purchase sound for credits

      // Missions
      'mission-accept': () => this.soundChip!.purchaseComplete(), // Use purchase sound
      'mission-complete': () => this.soundChip!.bountyAwarded(), // Use bounty sound

      // Alerts & Warnings
      'alert': () => this.soundChip!.alert(),
      'warning': () => this.soundChip!.alert(),
      'danger': () => this.soundChip!.alert(),
      'alarm': () => this.soundChip!.alert(),
      'fuel-warning': () => this.soundChip!.fuelWarning(),
      'low-fuel': () => this.soundChip!.fuelWarning(),
      'contraband': () => this.soundChip!.contrabandAlert(),
      'police': () => this.soundChip!.policeScanner(),
      'self-destruct': () => this.soundChip!.selfDestructWarning(),

      // Communications
      'comms': () => this.soundChip!.commsIncoming(),
      'comms-incoming': () => this.soundChip!.commsIncoming(),
      'static': () => this.soundChip!.commsStatic(),
      'station-chime': () => this.soundChip!.stationChime(),
      'radar': () => this.soundChip!.radarPing(),
      'scanner': () => this.soundChip!.radarPing(),

      // Ship Systems
      'systems': () => this.soundChip!.systemsOnline(),
      'systems-online': () => this.soundChip!.systemsOnline(),
      'cloak': () => this.soundChip!.cloakingDevice(),
      'escape-pod': () => this.soundChip!.escapePod(),

      // UI sounds
      'select': () => this.soundChip!.menuSelect(),
      'menu-select': () => this.soundChip!.menuSelect(),
      'confirm': () => this.soundChip!.menuConfirm(),
      'menu-confirm': () => this.soundChip!.menuConfirm(),
      'cancel': () => this.soundChip!.errorBeep(), // Use error for cancel
      'legal': () => this.soundChip!.legalStatusChange(),
      'rank-up': () => this.soundChip!.rankUp(),
      'type': () => this.soundChip!.typewriterKey(),
      'type-key': () => this.soundChip!.typewriterKey(),
      'type-return': () => this.soundChip!.typewriterReturn(),
      'game-over': () => this.soundChip!.gameOver(),
    };

    this.initialized = true;
  }

  /**
   * Play a sound by ID
   */
  async playSound(soundId: string): Promise<void> {
    if (!this.initialized || !this.soundChip) {
      console.warn('Sound service not initialized. Initializing now...');
      await this.init();
    }

    const soundFn = this.soundMap[soundId.toLowerCase()];
    if (!soundFn) {
      console.warn(`Sound not found: ${soundId}`);
      return;
    }

    try {
      await soundFn();
    } catch (error) {
      console.error(`Error playing sound ${soundId}:`, error);
    }
  }

  /**
   * Get list of all available sound IDs
   */
  getAvailableSounds(): string[] {
    return Object.keys(this.soundMap);
  }

  /**
   * Check if a sound ID is valid
   */
  hasSound(soundId: string): boolean {
    return soundId.toLowerCase() in this.soundMap;
  }
}

// Singleton instance
export const soundService = new SoundService();
